import {
  InfiniteData,
  QueryFunctionContext,
  QueryMeta,
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
  UseInfiniteQueryResult,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

import { delay, delayForIndexing } from "@examspark/common";
import { Replace, SearchableConnection } from "@examspark/types";

export type PaginatedSearch<
  TData extends SearchableConnection<unknown> = SearchableConnection<unknown>,
  TError = unknown,
> = UseInfiniteQueryResult<TData> & {
  currentPage: number;
  currentData: TData | undefined;
  totalCount: number | undefined;
  pageCount: number | undefined;
  previousPage: () => void;
  nextPage: () => void;
  canPagePrevious: boolean;
  canPageNext: boolean;
  isFetchingDelayed: boolean;
  refetch: <TPageData = unknown>(
    options?: SearchRefetchOptions<TPageData>,
  ) => Promise<QueryObserverResult<InfiniteData<TData>, TError>>;
};

export type SearchRefetchOptions<TPageData = unknown> = RefetchOptions &
  RefetchQueryFilters<TPageData> & { delay?: boolean | number };

export type PaginatedSearchQueryFunction<T> = (
  context: Replace<
    QueryFunctionContext,
    {
      meta: QueryMeta & {
        variables: { nextToken: string | undefined; limit: number };
      };
    }
  >,
) => T | undefined | Promise<T | undefined>;

/**
 * @deprecated use `usePaginatedSearch` in the specialized `common-app-search`
 * package.
 */
export const usePaginatedSearch = <
  TData extends SearchableConnection<unknown>,
>(params: {
  queryFn: PaginatedSearchQueryFunction<TData>;
  queryKey: unknown[];
  limit?: number;
  keepPreviousData?: boolean;
  enabled?: boolean;
}): PaginatedSearch<TData> => {
  const [page, setPage] = useState(1);
  const [isFetchingDelayed, setIsFetchingDelayed] = useState(false);

  const query = useInfiniteQuery({
    queryKey: params.queryKey,
    enabled: params.enabled ?? true,
    keepPreviousData: params.keepPreviousData,
    queryFn: async (context) => {
      const result = await params.queryFn({
        ...context,
        meta: {
          variables: {
            nextToken: context.pageParam,
            limit: params.limit ?? 50,
          },
        },
      });

      if (result == null) {
        throw new Error("Query function failed to return a result.");
      }

      return result;
    },
    getNextPageParam: (lastPage) => lastPage.nextToken ?? undefined,
  });

  // Reset the page when the query changes
  const stableKey = JSON.stringify(params.queryKey);
  useEffect(() => {
    setPage(1);
  }, [stableKey]);

  const currentData = query.data?.pages[page - 1];

  const pageCount = Math.ceil((currentData?.total ?? 0) / (params.limit ?? 50));
  const fetchedPageCount = query.data?.pages.length ?? 0;

  const canPageNext = page < pageCount;
  const canPagePrevious = page > 1;

  const nextPage = useCallback(() => {
    if (!canPageNext) return;
    setPage((page) => page + 1);

    // if we are at the end of the fetched pages, fetch the next page
    if (page >= fetchedPageCount) {
      query.fetchNextPage();
    }
  }, [canPageNext, fetchedPageCount, page, query]);

  const previousPage = useCallback(() => {
    if (!canPagePrevious) return;
    setPage((page) => page - 1);
  }, [canPagePrevious]);

  return {
    ...query,
    currentData,
    currentPage: page,
    totalCount: currentData?.total ?? 0,
    pageCount,
    nextPage,
    previousPage,
    canPagePrevious,
    canPageNext,
    isFetching:
      query.isFetching || query.isFetchingNextPage || isFetchingDelayed,
    isFetchingDelayed,
    refetch: async <TPageData>(options?: SearchRefetchOptions<TPageData>) => {
      if (options?.delay === true) {
        setIsFetchingDelayed(true);
        await delayForIndexing();
      } else if (options?.delay) {
        setIsFetchingDelayed(true);
        await delay(options.delay);
      }

      const result = await query.refetch(options);
      setIsFetchingDelayed(false);
      setPage(1);
      return result;
    },
  };
};
